import EmptyState from "@/components/empty-state/EmptyState.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import {
  ConfirmationModal,
  Page,
  useModals,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Table,
  TableCell,
} from "@skailer-com/uikit";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { CompensationChangeReasonEditModal } from "@/pages/settings/compensation-change-reasons/widgets/CompensationChangeReasonEditModal.tsx";
import {
  useCompensationChangeReasonDelete,
  useCompensationChangeReasons,
} from "@/queries/use-compensation-change-reasons.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiCompensationChangeReason } from "@/types/compensation-change-reason.types.ts";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

export function CompensationChangeReasons() {
  const { t } = useTranslation();
  const { openModal } = useModals();
  useTitle(t("core:compensation_change_reasons"), t("common:settings"));

  const { data: compensationChangeReasons, isLoading, isFetching } = useCompensationChangeReasons();
  const compensationChangeReasonDelete = useCompensationChangeReasonDelete();

  const columns = useMemo<ColumnDef<ApiCompensationChangeReason>[]>(() => {
    return [
      {
        header: t("common:title"),
        cell: (row) => <TableCell>{row.row.original.title}</TableCell>,
        accessorKey: "title",
        ...ColumnSizes.employee,
      },
    ];
  }, [compensationChangeReasons]);

  const handleEditOrCreate = useCallback(
    (reason: ApiCompensationChangeReason | undefined = undefined) => {
      openModal(CompensationChangeReasonEditModal, {
        compensationChangeReason: reason,
      });
    },
    []
  );

  const handleDelete = useCallback(async (reason: ApiCompensationChangeReason) => {
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await compensationChangeReasonDelete.mutateAsync(reason.id, {
            onSuccess() {
              analyticsService.trackEvent(analyticEvents.compensationChangeReasons.deleted, {
                [analyticProperties.id]: reason.id,
              });
            },
          });
        } catch {}
      },
    });
  }, []);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.compensationChangeReasons.viewed);
  });

  const renderRowAction = useCallback(
    (row: Row<ApiCompensationChangeReason>) => (
      <DropdownMenu>
        <DropdownMenuItem
          onClick={() => {
            handleEditOrCreate(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen"} />}
          title={t("common:edit")}
        />
        <DropdownMenuItem
          onClick={() => {
            void handleDelete(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
          variant={"menuDanger"}
          title={t("common:delete") + "..."}
        />
      </DropdownMenu>
    ),
    [handleDelete, handleEditOrCreate, t]
  );

  return (
    <Page>
      <Page.Header
        showBack={true}
        title={t("core:compensation_change_reasons")}
        navigateTo={"/settings/employment"}
      >
        <Stack>
          <Button
            onClick={() => handleEditOrCreate()}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
          >
            {t("common:create_new")}
          </Button>
        </Stack>
      </Page.Header>
      <Page.Content>
        <Table
          renderRowAction={renderRowAction}
          data={compensationChangeReasons || []}
          columns={columns}
          isFetching={isFetching || compensationChangeReasonDelete.isPending}
          showSkeleton={isLoading}
          notFoundComponent={<EmptyStateNothingFound />}
          emptyStateComponent={<EmptyState />}
        />
      </Page.Content>
    </Page>
  );
}
